import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MaterialDropdown from '../../components/materialDropdown/MaterialDropdown';
import { useUser } from '../../context/UserContext';
import { Language } from '../../interfaces/language';
import './ProfileSetupPage.css';

const ProfileSetupPage: React.FC = () => {
  const { userData, refreshUser } = useUser();
  const navigate = useNavigate();
  const [name, setName] = useState(userData?.name || '');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<string>(
    userData?.native_language_code || 'en-US'
  );
  const [timezone, setTimezone] = useState(userData?.timezone || '');
  const [timezones, setTimezones] = useState<string[]>([]);

  const fetchTimezones = useCallback(async () => {
    try {
      const response = await axios.get('/api/misc/timezones');
      setTimezones(response.data);
    } catch (error) {
      console.error('Error fetching timezones:', error);
    }
  }, []);

  const fetchLanguages = useCallback(async () => {
    try {
      const response = await axios.get('/api/languages/supported');
      // Convert API response object into an array of Language objects
      const languagesArray: Language[] = Object.keys(response.data).map(
        (code) => ({
          code,
          name: response.data[code],
        })
      );

      setLanguages(languagesArray);
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  }, []);

  useEffect(() => {
    fetchTimezones();
    fetchLanguages();
  }, [fetchTimezones, fetchLanguages]);

  // Redirect to dashboard if profile is already set up
  useEffect(() => {
    if (userData?.native_language_code) {
      navigate('/dashboard');
    }
  }, [navigate, userData?.native_language_code]);

  const handleSubmit = async () => {
    try {
      await axios.put('/api/users', {
        name: name,
        timezone: timezone,
        native_language_code: selectedLanguageCode,
      });
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  return (
    <div className="profile-setup-overlay">
      <div className="profile-setup-container">
        <h2>Complete Your Profile</h2>
        <div className="profile-setup-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="profile-setup-input"
              required
            />
          </div>

          <div className="form-group">
            <MaterialDropdown
              label="Native Language"
              value={selectedLanguageCode}
              options={languages.map((lang) => ({
                value: lang.code,
                label: lang.name,
              }))}
              onChange={(value) => setSelectedLanguageCode(value)}
              className="profile-setup-dropdown"
              fullWidth={true}
            />
          </div>

          <div className="form-group">
            <MaterialDropdown
              label="Timezone"
              value={timezone}
              options={timezones.map((tz) => ({
                value: tz,
                label: tz.replace('_', ' '),
              }))}
              onChange={(value) => setTimezone(value)}
              className="profile-setup-dropdown"
              fullWidth={true}
            />
          </div>
        </div>
        <button
          className="profile-setup-submit-button"
          disabled={!timezone || !name || !selectedLanguageCode}
          onClick={handleSubmit}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default ProfileSetupPage;
